import React, { useMemo, useState, useEffect } from 'react';
import { formatError, getCachedAnalysisData, cacheAnalysisData } from '../utils/index.ts';
import { AnalysisResult } from '../types/analysis.types.ts';
import { analyzeArgumentsFromCsv, getAnalysisResults } from '../services/analysis.service.ts';
import {
  calculateStanceStats,
  calculateTopicFrequency,
  getUniqueTopicsCount,
  getAverageArgumentLength,
} from '../utils/analysis.utils.ts';
import { getCachedRecentAnalysisData, cacheRecentAnalysisData } from '../utils/cache.utils.ts';
import StanceDistributionChart from '../components/analysis/StanceDistributionChart.tsx';
import TopicFrequencyChart from '../components/analysis/TopicFrequencyChart.tsx';
import Loading from '../components/common/Loading.tsx';
import UploadCsvModal from '../components/analysis/UploadCsvModal.tsx';

const AnalysisPage = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult[]>(() => {
    const cachedResults = getCachedRecentAnalysisData();
    return cachedResults || [];
  });
  const [showUploadModal, setShowUploadModal] = useState(false);

  // User's historical analysis data
  const [userAnalysisData, setUserAnalysisData] = useState([] as AnalysisResult[]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isRefreshingStats, setIsRefreshingStats] = useState(false);
  const [statsError, setStatsError] = useState(null as string | null);


  // Fetch user's analysis data on mount with cache support
  useEffect(() => {
    const fetchUserAnalysis = async () => {
      // Load cached data immediately
      const cachedData = getCachedAnalysisData();
      if (cachedData && cachedData.length > 0) {
        setUserAnalysisData(cachedData);
        setIsLoadingStats(false);
        setIsRefreshingStats(true);
      } else {
        setIsLoadingStats(true);
      }

      setStatsError(null);

      // Fetch fresh data in the background
      try {
        const response = await getAnalysisResults(1000, 0);
        setUserAnalysisData(response.results);
        // Cache the fresh data
        cacheAnalysisData(response.results);
      } catch (err) {
        // Only show error if we don't have cached data
        if (!cachedData || cachedData.length === 0) {
          setStatsError(formatError(err));
        }
      } finally {
        setIsLoadingStats(false);
        setIsRefreshingStats(false);
      }
    };

    fetchUserAnalysis();
  }, []);

  // Update analysisResults when userAnalysisData changes
  useEffect(() => {
    if (userAnalysisData.length > 0) {
      // Set analysisResults to the most recent items (e.g., last 10 items)
      const recentResults = userAnalysisData.slice(0, 10);
      setAnalysisResults(recentResults);
      // Also cache these to localStorage
      cacheRecentAnalysisData(recentResults);
    }
  }, [userAnalysisData]);

  // Calculate statistics from user's analysis data
  const stanceStats = useMemo(
    () => calculateStanceStats(userAnalysisData),
    [userAnalysisData]
  );
  const topicFrequency = useMemo(
    () => calculateTopicFrequency(userAnalysisData, 10),
    [userAnalysisData]
  );
  const uniqueTopicsCount = useMemo(
    () => getUniqueTopicsCount(userAnalysisData),
    [userAnalysisData]
  );
  const avgArgumentLength = useMemo(
    () => getAverageArgumentLength(userAnalysisData),
    [userAnalysisData]
  );

  const handleOpenUploadModal = () => {
    setShowUploadModal(true);
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  const handleAnalyzeFromModal = async (file: File) => {
    setIsAnalyzing(true);

    try {
      const response = await analyzeArgumentsFromCsv(file);
      setAnalysisResults(response.results);
      // Cache the recent analysis results
      cacheRecentAnalysisData(response.results);
      // Refresh user analysis data after successful analysis
      const updatedResponse = await getAnalysisResults(1000, 0);
      setUserAnalysisData(updatedResponse.results);
      // Cache the updated data
      cacheAnalysisData(updatedResponse.results);
    } catch (err) {
      // For now, just log the error since we don't have a state to store it
      // In a production app, we might want to use a toast notification system
      console.error('Analysis error:', formatError(err));
    } finally {
      setIsAnalyzing(false);
      setShowUploadModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-black px-4 pt-20 pb-10 transition-colors duration-200">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Statistics Section */}
        <div className="rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm">
          <div className="border-b border-slate-200 dark:border-zinc-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
                  Your Analysis Statistics
                </h2>
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                  Overview of all your analyzed arguments
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isRefreshingStats && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 dark:border-zinc-600 border-t-blue-600 dark:border-t-blue-500"></div>
                    <span>Updating...</span>
                  </div>
                )}
                <button
                  onClick={handleOpenUploadModal}
                  className="rounded-md bg-blue-600 dark:bg-blue-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 dark:hover:bg-blue-600"
                >
                  Upload CSV
                </button>
              </div>
            </div>
          </div>

          {isLoadingStats ? (
            <div className="flex items-center justify-center py-12">
              <Loading />
            </div>
          ) : statsError ? (
            <div className="px-6 py-4">
              <p className="text-sm text-red-600 dark:text-red-400">{statsError}</p>
            </div>
          ) : userAnalysisData.length === 0 ? (
            <div className="px-6 py-4">
              <p className="text-sm text-slate-500 dark:text-zinc-400">
                No analysis data available yet. Upload and analyze a CSV file to see statistics.
              </p>
            </div>
          ) : (
              <div className="p-6 space-y-8">
                {/* Summary Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 dark:bg-zinc-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-slate-800 dark:text-white">
                      {stanceStats.total}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-zinc-400">Total Arguments</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-zinc-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-slate-800 dark:text-white">
                      {uniqueTopicsCount}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-zinc-400">Unique Topics</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-zinc-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-slate-800 dark:text-white">
                      {avgArgumentLength}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-zinc-400">Avg Length (chars)</div>
                  </div>
                  <div className="bg-slate-50 dark:bg-zinc-800 rounded-lg p-4">
                    <div className="text-2xl font-bold text-slate-800 dark:text-white">
                      {stanceStats.pro}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-zinc-400">PRO Arguments</div>
                  </div>
                </div>

                {/* Charts Grid */}
                <div className="grid grid-cols-1 gap-6">
                  {/* Stance Distribution */}
                  <div className="rounded-lg border border-slate-200 dark:border-zinc-700 p-4">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-4">
                      Stance Distribution
                    </h3>
                    <StanceDistributionChart stats={stanceStats} />
                    <div className="mt-4 flex justify-center gap-6 text-sm">
                      <div>
                        <span className="font-semibold text-green-700 dark:text-green-400">
                          PRO: {stanceStats.pro} ({stanceStats.proPercentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div>
                        <span className="font-semibold text-red-700 dark:text-red-400">
                          CON: {stanceStats.con} ({stanceStats.conPercentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Topic Frequency */}
                {topicFrequency.length > 0 && (
                  <div className="rounded-lg border border-slate-200 dark:border-zinc-700 p-4">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-zinc-300 mb-4">
                      Most Discussed Topics (Top 10)
                    </h3>
                    <TopicFrequencyChart data={topicFrequency} />
                  </div>
                )}

                {/* Analysis Results Table */}
                {analysisResults.length > 0 && (
                  <div className="rounded-lg border border-slate-200 dark:border-zinc-700">
                    <div className="bg-green-50 dark:bg-green-900/20 px-4 py-2 border-b border-slate-200 dark:border-zinc-700">
                      <h3 className="text-sm font-semibold text-green-800 dark:text-green-400">
                        Recent Analysis Results ({analysisResults.length} results)
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-zinc-700">
                        <thead className="bg-slate-50 dark:bg-zinc-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              Argument
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              Topic
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              Stance
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              Confidence
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              PRO
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold uppercase tracking-wide text-slate-600 dark:text-zinc-300">
                              CON
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-zinc-700 bg-white dark:bg-zinc-900">
                          {analysisResults.map((result) => (
                            <tr key={result.id} className="hover:bg-slate-50 dark:hover:bg-zinc-800">
                              <td className="whitespace-pre-wrap px-4 py-3 text-sm text-slate-700 dark:text-zinc-300 max-w-md">
                                {result.argument}
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-zinc-300">
                                {result.topic}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span
                                  className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                    result.predicted_stance === 'PRO'
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                                  }`}
                                >
                                  {result.predicted_stance}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-zinc-300">
                                {(result.confidence * 100).toFixed(1)}%
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-zinc-300">
                                {(result.probability_pro * 100).toFixed(1)}%
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-zinc-300">
                                {(result.probability_con * 100).toFixed(1)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* CSV Upload and Analysis Section */}

        {/* Upload CSV Modal */}
        {showUploadModal && (
          <UploadCsvModal
            isOpen={showUploadModal}
            onClose={handleCloseUploadModal}
            onAnalyze={handleAnalyzeFromModal}
            isAnalyzing={isAnalyzing}
          />
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;

